import React, { useEffect, useState } from "react";
import { Card, Table } from "react-bootstrap";
import { getEmpVouchers, getMyVouchers } from "../../../../../src/apis/travel/MyVouchersApi";
import VoucherDetailRow from "./VoucherDetailRow";
import Dollars from "../../../common/typeFormatting/Dollars";

const calculateTimeOfService = (hireDate) => {
  const today = new Date();
  const hire = new Date(hireDate);
  
  let years = today.getFullYear() - hire.getFullYear();
  let months = today.getMonth() - hire.getMonth();
  
  if (months < 0 || (months === 0 && today.getDate() < hire.getDate())) {
    years--;
    months += 12;
  }
  
  if (today.getDate() < hire.getDate()) {
    months--;
    if (months < 0) {
      months = 11;
      years--;
    }
  }
  
  const yearsText = years === 1 ? "Year" : "Years";
  const monthsText = months === 1 ? "Month" : "Months";
  
  if (years === 0) {
    return `${months} ${monthsText}`;
  } else if (months === 0) {
    return `${years} ${yearsText}`;
  } else {
    return `${years} ${yearsText}, ${months} ${monthsText}`;
  }
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
};

const MyVouchersStatement = ({ employeeId, isGhosting }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [voucherStatement, setVoucherStatement] = useState(null);
  const [timeOfService, setTimeOfService] = useState("");
  const [travelVouchers, setTravelVouchers] = useState([]);
  const [partnerTechVouchers, setPartnerTechVouchers] = useState([]);
  const [travelTotals, setTravelTotals] = useState({ totalBalance: 0, totalBenefitReceived: 0 });
  const [partnerTechTotals, setPartnerTechTotals] = useState({ totalBalance: 0, totalBenefitReceived: 0 });

  useEffect(() => {
    async function getVoucherData(eid, ghosting) {
      try {
        const stmt = ghosting ? await getEmpVouchers(eid) : await getMyVouchers();
        stmt.vouchers.sort((a, b) => new Date(b.issueDate) - new Date(a.issueDate));
        
        // Separate vouchers by allowance type
        const travel = stmt.vouchers.filter(v => v.allowanceTypeId === 'TRAVEL');
        const partnerTech = stmt.vouchers.filter(v => v.allowanceTypeId === 'PARTTECH');
        
        setVoucherStatement(stmt);
        console.log("stmt", stmt);
        setTravelVouchers(travel);
        setPartnerTechVouchers(partnerTech);
        setTimeOfService(calculateTimeOfService(stmt.allowanceSeniorityDate));
        
        // Calculate totals for each type
        const travelBalance = travel.reduce((sum, v) => sum + v.balance, 0);
        const travelBenefit = travel.reduce((sum, v) => sum + v.initialAmount, 0);
        setTravelTotals({ totalBalance: travelBalance, totalBenefitReceived: travelBenefit });
        
        const techBalance = partnerTech.reduce((sum, v) => sum + v.balance, 0);
        const techBenefit = partnerTech.reduce((sum, v) => sum + v.initialAmount, 0);
        setPartnerTechTotals({ totalBalance: techBalance, totalBenefitReceived: techBenefit });
        
        setIsLoading(false);
      } catch(error) {
        console.log(error);
        setIsLoading(false);
      }
    }

    getVoucherData(employeeId, isGhosting);
  }, [employeeId, isGhosting]);

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="p-4">
      {/* Travel Allowances Card */}
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <Card.Title>My Travel Allowances</Card.Title>
          </div>
          
          <div className="row mb-4">
            <div className="col-md-4 text-center">
              <div className="text-muted mb-2">Years of Service at RBA</div>
              <h4 className="mb-1">{timeOfService}</h4>
              <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                starting on {formatDate(voucherStatement.allowanceSeniorityDate)}
              </div>
            </div>
            <div className="col-md-4 text-center">
              <div className="text-muted mb-2">Total Benefit Received</div>
              <h4><Dollars>{travelTotals.totalBenefitReceived}</Dollars></h4>
            </div>
            <div className="col-md-4 text-center">
              <div className="text-muted mb-2">Available Balance</div>
              <h4><Dollars>{travelTotals.totalBalance}</Dollars></h4>
            </div>
          </div>

          <Table hover responsive style={{ fontSize: 14 }}>
            <thead>
              <tr>
                <th style={{ width: '15%' }}>Issue Date</th>
                <th style={{ width: '25%' }}>Description</th>
                <th style={{ width: '15%', textAlign: 'right' }}>Initial Amount</th>
                <th style={{ width: '15%', textAlign: 'right', paddingRight: '2rem' }}>Current Balance</th>
                <th style={{ width: '25%', paddingLeft: '4rem' }}>Expiration Date</th>
                <th style={{ width: '5%' }}></th>
              </tr>
            </thead>
            <tbody>
              {travelVouchers.length > 0 ? (
                travelVouchers.map(voucher => (
                  <VoucherDetailRow key={voucher.voucherId} voucher={voucher} />
                ))
              ) : (
                <tr><td colSpan="6">No travel allowances found.</td></tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Partner Tech Allowances Card */}
      {partnerTechVouchers.length > 0 && (
        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <Card.Title>My Partner Tech Allowances</Card.Title>
            </div>
            
            <div className="row mb-4">
              <div className="col-md-4 text-center">
                <div className="text-muted mb-2">Partner Level</div>
                <h4 className="mb-1">{voucherStatement.partnerLevel || "N/A"}</h4>
              </div>
              <div className="col-md-4 text-center">
                <div className="text-muted mb-2">Total Benefit Received</div>
                <h4><Dollars>{partnerTechTotals.totalBenefitReceived}</Dollars></h4>
              </div>
              <div className="col-md-4 text-center">
                <div className="text-muted mb-2">Available Balance</div>
                <h4><Dollars>{partnerTechTotals.totalBalance}</Dollars></h4>
              </div>
            </div>

            <Table hover responsive style={{ fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={{ width: '15%' }}>Issue Date</th>
                  <th style={{ width: '25%' }}>Description</th>
                  <th style={{ width: '15%', textAlign: 'right' }}>Initial Amount</th>
                  <th style={{ width: '15%', textAlign: 'right', paddingRight: '2rem' }}>Current Balance</th>
                  <th style={{ width: '25%', paddingLeft: '4rem' }}>Expiration Date</th>
                  <th style={{ width: '5%' }}></th>
                </tr>
              </thead>
              <tbody>
                {partnerTechVouchers.map(voucher => (
                  <VoucherDetailRow key={voucher.voucherId} voucher={voucher} />
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default MyVouchersStatement;